import { TournamentCompetitionDefinition, TournamentCustomGroup, TournamentMatchSource, TournamentStructureMatch } from 'Src/models/tournament';

const roundTitles: Record<string, string> = { ROUND_OF_64: 'Round of 64', ROUND_OF_32: 'Round of 32', ROUND_OF_16: 'Round of 16', ROUND_OF_8: 'Quarter-final', QUARTER_FINAL: 'Quarter-final', SEMI_FINAL: 'Semi-final', FINAL: 'Final', PLAYOFF: 'Play-off', THIRD_PLACE: 'Third-place play-off' };

const ordinal = (position: number) =>
  `${position}${
    position % 100 >= 11 && position % 100 <= 13
      ? 'th'
      : position % 10 === 1
      ? 'st'
      : position % 10 === 2
      ? 'nd'
      : position % 10 === 3
      ? 'rd'
      : 'th'
  }`;

export function tournamentStructurePublicRoutes(
  definition: TournamentCompetitionDefinition,
  groups: TournamentCustomGroup[]
): TournamentCompetitionDefinition['groupRoutes'] {
  const pools = definition.qualifierGroups || [];
  const poolIds = new Set(pools.map(pool => pool.groupId));
  const matches = definition.matches.filter(match => !match.removed);
  const matchById = new Map(matches.map(match => [match.id, match]));
  return groups.map(group => {
    const saved = definition.groupRoutes.find(route => route.groupId === group.id);
    if (
      saved &&
      (saved.isAutomatic === false ||
        (saved.isAutomatic === undefined && (saved.publicExplanation || saved.destinations?.length)))
    )
      return saved;
    const destinations = new Map<string, { title: string; positions: Set<number>; outcomes: Set<string> }>();
    const qualifierPlaces = new Set(pools.flatMap(pool => pool.sources.filter(source =>
      source.sourceType === 'GROUP_POSITION' && source.sourceGroupId === group.id).map(source => source.sourcePosition)));
    const add = (targetId: string, source: TournamentMatchSource, match?: TournamentStructureMatch) => {
      const sourceMatch = source.sourceType === 'MATCH_WINNER' || source.sourceType === 'MATCH_LOSER'
        ? matchById.get(source.sourceMatchId) : undefined;
      const applies =
        source.sourceType === 'GROUP_POSITION' ? source.sourceGroupId === group.id : sourceMatch?.groupId === group.id;
      const section = groups.find(item => item.id === targetId);
      if (!applies || targetId === group.id || !section) return;
      const round = roundTitles[match?.round || ''];
      const name = match?.name?.trim();
      const fixture = match && match.round !== 'GROUP' && (round || (name && name !== section.name));
      const key = fixture ? `match:${match.id || matches.indexOf(match)}` : `group:${targetId}`;
      const sectionBase = section.name.replace(/\s+final$/i, '');
      const title = fixture && name && name.toLowerCase() !== section.name.toLowerCase()
        ? (match.round !== 'FINAL' && name.startsWith(`${section.name} · `) ? `${sectionBase}${name.slice(section.name.length)}` : name)
        : round && !(match.round === 'FINAL' && /\bfinal$/i.test(section.name))
        ? `${sectionBase} ${round}`
        : section.name;
      if (!destinations.has(key)) destinations.set(key, { title, positions: new Set(), outcomes: new Set() });
      const target = destinations.get(key)!;
      if (source.sourceType === 'GROUP_POSITION') target.positions.add(source.sourcePosition!);
      else target.outcomes.add(source.sourceType === 'MATCH_WINNER' ? 'Winners' : 'Losers');
    };
    pools.forEach(pool => pool.sources.forEach(source => add(pool.groupId, source)));
    matches.filter(match => !poolIds.has(match.groupId)).forEach(match => {
      [match.homeSource, match.awaySource].forEach(source => {
        // A place assigned to a qualifier enters that pool first.
        if (source.sourceType !== 'GROUP_POSITION' || !qualifierPlaces.has(source.sourcePosition)) add(match.groupId, source, match);
      });
    });
    const routes = Array.from(destinations.entries())
      .sort(([, a], [, b]) => (Math.min(...a.positions) - Math.min(...b.positions)) || 0)
      .map(([, details]) => {
        const { title } = details;
        const places = [
          ...Array.from(details.positions)
            .sort((a, b) => a - b)
            .map(ordinal),
          ...Array.from(details.outcomes)
        ].join(', ');
        return { title, places, publicExplanation: `${places} from ${group.name} advance to ${title}.` };
      });
    return {
      groupId: group.id,
      isAutomatic: true,
      publicExplanation: routes.map(route => route.publicExplanation).join(' '),
      destinations: routes
    };
  });
}
